import type { SearchResult } from 'types/api/search';
import type { SearchQuery } from 'ui/snippets/searchBar/useSearchQuery';
import React from 'react';
import { isBech32Address, fromBech32Address } from 'lib/address/bech32';
import useApiQuery from 'lib/api/useApiQuery';
import useDebounce from 'lib/hooks/useDebounce';

export default function useQuickSearchQuery() {
  const [searchTerm, setSearchTerm] = React.useState('');
  const [loadingSecond, setLoadingSecond] = React.useState(false);
  const [secondApiResponse, setSecondApiResponse] = React.useState<any>([]);

  const debouncedSearchTermL1X = useDebounce(searchTerm, 300);
  const debouncedSearchTerm = useDebounce(searchTerm, 800);

  const transformedSearchTerm = React.useMemo(() => {
    return isBech32Address(debouncedSearchTerm)
      ? fromBech32Address(debouncedSearchTerm)
      : debouncedSearchTerm;
  }, [debouncedSearchTerm]);

  const query = useApiQuery('general:quick_search', {
    queryParams: { q: transformedSearchTerm },
    queryOptions: {
      enabled: debouncedSearchTerm.trim().length > 0,
    },
  });

  const redirectCheckQuery = useApiQuery('general:search_check_redirect', {
    queryParams: { q: debouncedSearchTerm },
    queryOptions: {
      enabled: Boolean(debouncedSearchTerm),
    },
  });

  React.useEffect(() => {
    const trimmed = debouncedSearchTermL1X.trim();

    const txHash = trimmed.startsWith('0x') ? trimmed.slice(2) : trimmed;
    setLoadingSecond(true);

    fetch(`/api/l1x-tx-details?tx_hash=${txHash}`)
      .then((res) => res.json())
      .then((data: any) => {
        setSecondApiResponse(data)
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.error('❌ Error calling proxy API:', err);
      })
      .finally(() => {
        setLoadingSecond(false);
      });
  }, [debouncedSearchTermL1X]);

  // const isLoading = query.isLoading || loadingSecond;

  return React.useMemo(() => {
    const finalQuery = secondApiResponse?.tx_type === "L1XVM" ?
      {
        ...query,
        data: { items: secondApiResponse ? [secondApiResponse] : [], next_page_params: null } as SearchResult,
        isSuccess: true,
      } :
      query;

    return {
      searchTerm,
      debouncedSearchTerm,
      handleSearchTermChange: setSearchTerm,
      query: finalQuery as SearchQuery,
      redirectCheckQuery,
    };
  }, [debouncedSearchTerm, query, redirectCheckQuery, searchTerm, secondApiResponse]);
}
